import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, Sparkle, Brain } from "@phosphor-icons/react";

const fade = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.7 } };

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Secure",
    body: "Built with a security-first architecture, offering flexible deployment models that prioritize data sovereignty.",
  },
  {
    icon: Sparkle,
    title: "Intuitive",
    body: "Designed to integrate seamlessly with existing legal practice management ecosystems.",
  },
  {
    icon: Brain,
    title: "Intelligent",
    body: "Powered by proprietary AI models capable of identifying risks and comparing complex legal instruments in seconds.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="border-b border-border">
        <div className="container-x py-20 md:py-32 max-w-5xl">
          <p className="overline">About Cognita AI</p>
          <motion.h1
            {...fade}
            className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.98] mt-5 font-light"
          >
            The future of legal operations,
            <br />
            <span className="italic text-[#002147]">reimagined.</span>
          </motion.h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            At Cognita AI, we bridge the gap between complex legal workflows and the frontier of artificial intelligence.
            We don&apos;t just build software — we build high-performance infrastructure designed to transform how modern legal
            firms manage the entire lifecycle of a contract.
          </p>
          <p className="mt-5 text-base text-muted-foreground max-w-3xl leading-relaxed">
            From initial drafting and risk assessment to clause identification and final execution, our platform provides
            the precision, security, and speed required in today&apos;s legal landscape.
          </p>
        </div>
      </section>

      {/* MISSION */}
      <section className="container-x py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <p className="overline">Our mission</p>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 tracking-tight leading-[1.05]">
            Enterprise-grade automation for every firm.
          </h2>
        </div>
        <div className="md:col-span-7 space-y-5 text-muted-foreground leading-relaxed text-base">
          <p>
            Our mission is to empower small and mid-sized legal firms with enterprise-grade automation. By leveraging
            agentic AI workflows, we eliminate the friction of manual contract management — allowing legal professionals to
            focus on high-value strategy rather than administrative overhead.
          </p>
        </div>
      </section>

      {/* FOUNDATION */}
      <section className="bg-secondary/50 border-y border-border">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="overline">The foundation</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 tracking-tight leading-[1.05]">
              Global expertise.
              <br /> <span className="italic text-[#002147]">Local craft.</span>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-muted-foreground leading-relaxed text-base">
            <p>
              Cognita AI is led by two co-founders with a shared vision and a global footprint. Based in the United
              States and India, we combine deep technical expertise with a nuanced understanding of international legal
              standards.
            </p>
            <p>
              Our leadership team brings years of experience from the world&apos;s most influential technology pioneers —
              including <span className="text-foreground font-medium">IBM</span>,{" "}
              <span className="text-foreground font-medium">Cisco</span>, and{" "}
              <span className="text-foreground font-medium">AWS</span>. Having built and scaled solutions within these
              organizations, we have distilled that knowledge into a Contract Lifecycle Management platform that is
              secure, intuitive, and intelligent by design.
            </p>

            <div className="grid grid-cols-3 gap-px bg-border border border-border mt-10">
              {["IBM", "Cisco", "AWS"].map((b) => (
                <div key={b} className="bg-white px-6 py-8 text-center">
                  <p className="font-serif text-3xl tracking-tight">{b}</p>
                  <p className="overline mt-2 text-muted-foreground">Alumni</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="container-x py-24 md:py-32">
        <p className="overline">Built on three principles</p>
        <h2 className="font-serif text-4xl md:text-5xl mt-4 tracking-tight leading-[1.05] max-w-3xl">
          A platform engineered for the standard legal work demands.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border mt-12">
          {PILLARS.map((p, i) => (
            <motion.div
              {...fade}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              key={p.title}
              className="bg-white p-10"
            >
              <p.icon size={24} weight="light" />
              <span className="font-mono text-xs text-muted-foreground tracking-[0.2em] block mt-6">0{i + 1}</span>
              <h3 className="font-serif text-3xl mt-3 tracking-tight">{p.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY COGNITA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="overline text-white/60">Why Cognita AI?</p>
            <h2 className="font-serif text-4xl md:text-6xl mt-4 tracking-tighter leading-[1.02]">
              Legal tech should be an asset,
              <br /> <span className="italic text-white/70">not a hurdle.</span>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-white/75 leading-relaxed text-base">
            <p>
              While traditional CLM tools are often rigid and over-engineered, Cognita AI is built to be adaptive.
              Whether you are automating routine renewals or conducting deep-dive risk analysis across thousands of
              documents, our platform evolves with your firm&apos;s needs.
            </p>
            <p className="text-white/90 font-serif text-2xl md:text-3xl leading-snug pt-4 border-t border-white/15">
              Join us as we define the next era of legal intelligence.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-x py-24">
        <div className="border border-border p-10 md:p-16 bg-white grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <p className="overline">Talk to the founders</p>
            <h3 className="font-serif text-3xl md:text-4xl mt-3 tracking-tight">
              Curious about what we&apos;re building?
            </h3>
            <p className="mt-3 text-muted-foreground">
              We hire applied scientists, product engineers, and lawyers in residence — and we&apos;d love to hear from firms
              ready to partner with us early.
            </p>
          </div>
          <div className="md:col-span-4">
            <Link
              to="/contact"
              data-testid="about-contact-btn"
              className="inline-flex items-center justify-center w-full px-6 py-3.5 bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#002147] transition-colors"
            >
              Get in touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
